import { useEffect, useRef, useState } from "react";
import anime from "animejs"; // v3.2.1
import { Menu, X, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggleMenu = () => setMenuOpen((s) => !s);
  const { pathname, hash } = useLocation();

  const navItems = [
    // { name: "Services", href: "/pricing", type: "route" },
    { name: "How It Works", href: "/aboutUs", type: "route" },
    // { name: "Testimonials", href: "/aboutUs", type: "route" },
    { name: "Contact", href: "/contactUs", type: "route" },
    { name: "Pricing", href: "/pricing", type: "route" },
    // { name: "About us", href: "/aboutUs", type: "route" },
    // { name: "My Details", href: "/details", type: "route" },
    { name: "Admin", href: "/admin/login", type: "route" },
  ];

  // --- Active link detection (routes + hash)
  const isActive = (item) => {
    if (item.type === "scroll") return hash === item.href;
    return pathname === item.href;
  };

  // --- Animated underline indicator
  const navRef = useRef(null);
  const indicatorRef = useRef(null);
  const activeRef = useRef(null);

  const moveIndicator = (targetEl) => {
    const indicator = indicatorRef.current;
    const nav = navRef.current;
    if (!indicator || !nav || !targetEl) return;
    const navRect = nav.getBoundingClientRect();
    const itemRect = targetEl.getBoundingClientRect();
    const left = itemRect.left - navRect.left;
    anime({
      targets: indicator,
      translateX: left,
      width: itemRect.width,
      duration: 300,
      easing: "easeOutQuad",
    });
  };

  // set indicator to active on mount/route change
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activeItem = nav.querySelector("[data-active='true']");
    activeRef.current = activeItem;
    // init position instantly, then animate opacity in
    if (activeItem) {
      const indicator = indicatorRef.current;
      const navRect = nav.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      indicator.style.transform = `translateX(${
        itemRect.left - navRect.left
      }px)`;
      indicator.style.width = `${itemRect.width}px`;
      anime({
        targets: indicator,
        opacity: [0, 1],
        duration: 200,
        easing: "linear",
      });
    }
  }, [pathname, hash]);

  // hover -> move, leave -> snap back to active
  const onItemEnter = (e) => moveIndicator(e.currentTarget);
  const onNavLeave = () => moveIndicator(activeRef.current);

  // header shrink on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // CTA magnetic micro-interaction
  const onMagnetMove = (e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const mx = e.clientX - r.left - r.width / 2;
    const my = e.clientY - r.top - r.height / 2;
    anime({
      targets: btn,
      translateX: mx * 0.06,
      translateY: my * 0.06,
      duration: 150,
      easing: "easeOutQuad",
    });
  };
  const onMagnetLeave = (e) =>
    anime({
      targets: e.currentTarget,
      translateX: 0,
      translateY: 0,
      duration: 180,
      easing: "easeOutQuad",
    });

  return (
    <header
      className={[
        "sticky top-0 z-50 backdrop-blur-lg transition-all",
        scrolled
          ? "bg-white/85 shadow border-b border-gray-200/80"
          : "bg-white/70",
      ].join(" ")}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3 md:py-4">
        {/* Logo */}
        <Link to="/" className="group">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg grid place-items-center text-white font-bold text-lg shadow-md group-hover:scale-105 transition">
              PW
            </div>
            <span className="text-2xl font-extrabold text-gray-800 tracking-tight">
              Profile<span className="text-indigo-600">Wise</span>
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav
          ref={navRef}
          onMouseLeave={onNavLeave}
          className="relative hidden md:flex items-center gap-8 text-sm font-medium text-gray-600"
        >
          {/* animated underline */}
          <span
            ref={indicatorRef}
            className="absolute -bottom-2 h-[2px] rounded bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0"
            style={{ width: 0, transform: "translateX(0px)" }}
          />
          {navItems.map((item) => {
            const active = isActive(item);
            const common =
              "relative py-1 hover:text-indigo-700 transition-colors cursor-pointer";
            return item.type === "route" ? (
              <Link
                key={item.name}
                to={item.href}
                data-active={active ? "true" : "false"}
                className={[common, active ? "text-gray-900" : ""].join(" ")}
                onMouseEnter={onItemEnter}
                ref={(el) => {
                  if (active) activeRef.current = el;
                }}
              >
                {item.name}
              </Link>
            ) : (
              <a
                key={item.name}
                href={item.href}
                data-active={active ? "true" : "false"}
                className={[common, active ? "text-gray-900" : ""].join(" ")}
                onMouseEnter={onItemEnter}
                ref={(el) => {
                  if (active) activeRef.current = el;
                }}
              >
                {item.name}
              </a>
            );
          })}
        </nav>

        {/* CTA Button */}
        <Link
          to="/pricing"
          onMouseMove={onMagnetMove}
          onMouseLeave={onMagnetLeave}
          className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-xl font-semibold
                     bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg
                     hover:shadow-indigo-300/40 transition will-change-transform"
        >
          <User size={18} /> Get Started
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 rounded-md hover:bg-gray-100 transition"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <MobileMenu open={menuOpen} setOpen={setMenuOpen} navItems={navItems} />
    </header>
  );
}

function MobileMenu({ open, setOpen, navItems }) {
  // slide + fade with anime.js
  const panelRef = useRef(null);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (open) {
      el.style.display = "block";
      anime({
        targets: el,
        opacity: [0, 1],
        translateY: [-10, 0],
        duration: 220,
        easing: "easeOutQuad",
      });
    } else {
      anime({
        targets: el,
        opacity: [1, 0],
        translateY: [0, -10],
        duration: 180,
        easing: "easeOutQuad",
        complete: () => (el.style.display = "none"),
      });
    }
  }, [open]);

  return (
    <div
      ref={panelRef}
      style={{ display: "none" }}
      className="md:hidden bg-white border-t border-gray-200 shadow-lg"
    >
      <nav className="flex flex-col p-4 space-y-3 text-gray-700">
        {navItems.map((item) =>
          item.type === "route" ? (
            <Link
              key={item.name}
              to={item.href}
              className="rounded-lg px-3 py-2 hover:bg-gray-50 active:bg-gray-100"
              onClick={() => setOpen(false)}
            >
              {item.name}
            </Link>
          ) : (
            <a
              key={item.name}
              href={item.href}
              className="rounded-lg px-3 py-2 hover:bg-gray-50 active:bg-gray-100"
              onClick={() => setOpen(false)}
            >
              {item.name}
            </a>
          )
        )}
        <Link
          to="/pricing"
          onClick={() => setOpen(false)}
          className="flex items-center justify-center bg-indigo-600 text-white px-4 py-2 rounded-lg shadow mt-2 hover:bg-indigo-700"
        >
          <User className="mr-2" size={18} /> Get Started
        </Link>
        <Link
          to="/aboutUs"
          onClick={() => setOpen(false)}
          className="flex items-center justify-center bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg shadow mt-2 hover:bg-indigo-100"
        >
          About us
        </Link>
      </nav>
    </div>
  );
}
